import styled, { css } from 'styled-components';

import { COLORS, SPACE } from '../../../../../theme/tokens';

/** The in-flow trigger's box, the trash button's geometry. */
export const TRIGGER_PX = 36;
/** The corner trigger: a bare 18px glyph (Figma 56:2639 / 56:2403), no chrome at all. */
export const GLYPH_PX = 18;

/** The trigger: in flow, a 36px round icon button, muted at rest. `$corner` makes it the BARE glyph
 *  pinned to the top-right of the caller's positioned box (no border, no background, no padding
 *  box; still a real button for hit area and focus); `$danger` paints the glyph red. */
export const Trigger = styled.button<{ $corner: boolean; $danger: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 99px;
  border: 1px solid ${COLORS.hoverBorderLit};
  background: none;
  color: ${COLORS.textMuted};
  cursor: pointer;

  &:hover,
  &:focus-visible,
  &[aria-expanded='true'] {
    color: ${COLORS.textStrong};
    background: ${COLORS.hoverWash};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }

  ${(p) =>
    p.$corner &&
    css`
      position: absolute;
      top: ${SPACE[12]};
      right: ${SPACE[16]};
      width: ${GLYPH_PX}px;
      height: ${GLYPH_PX}px;
      border: 0;
      border-radius: 4px;
      background: none;

      &:hover,
      &:focus-visible,
      &[aria-expanded='true'] {
        background: none;
      }
    `}

  ${(p) =>
    p.$danger &&
    css`
      color: ${COLORS.danger};

      &:hover,
      &:focus-visible,
      &[aria-expanded='true'] {
        color: ${COLORS.danger};
      }

      &:focus-visible {
        outline-color: ${COLORS.danger};
      }
    `}
`;

/**
 * The panel: absolutely positioned against the caller's nearest positioned ancestor, right-aligned
 * to it and never wider than it (`max-width: 100%`), so it cannot escape that box at any width. That
 * is the whole positioning strategy: no measuring, no flipping. It stays in the DOM while closed
 * (`hidden`) so the trigger's `aria-describedby` always resolves. The floating-surface material is
 * the card surface, one step brighter at the edge, under the popover shadow.
 */
export const Panel = styled.div<{ $corner: boolean }>`
  position: absolute;
  top: calc(100% + ${SPACE[8]});
  right: 0;
  z-index: 2;
  box-sizing: border-box;
  width: 320px;
  max-width: 100%;

  /* Corner mode: under the pinned glyph (inset + its height + one step), inset from the right edge
     like the glyph, and never wider than the box minus both insets. */
  ${(p) =>
    p.$corner &&
    css`
      top: calc(${SPACE[12]} + ${GLYPH_PX}px + ${SPACE[8]});
      right: ${SPACE[16]};
      max-width: calc(100% - ${SPACE[16]} - ${SPACE[16]});
    `}
  padding: ${SPACE[12]} ${SPACE[16]};
  border-radius: 12px;
  border: 1px solid ${COLORS.surfaceMuted};
  background: ${COLORS.surfaceCard};
  box-shadow: ${COLORS.menuShadow};
  /* Explanatory copy, so the muted body ink (one strong colour per block; the card it explains
     holds the strong value). */
  color: ${COLORS.textMuted};
  font-size: 13px;
  line-height: 20px;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;

  &[hidden] {
    display: none;
  }
`;
